(function () {
  'use strict';
  if (window.PFAddressRegions) return;

  var pending;
  var messages = window.PF_ADDRESS_REGION_MESSAGES || {};

  function key(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  function fieldError(field, message) {
    var error = new Error(message);
    error.fields = {};
    error.fields[field] = message;
    return error;
  }

  function withTimeout(request, milliseconds) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error(messages.unavailable || 'Address lookup timed out.')); }, milliseconds);
      Promise.resolve(request).then(function (value) {
        clearTimeout(timer);
        resolve(value);
      }, function (error) {
        clearTimeout(timer);
        reject(error);
      });
    });
  }

  function countries() {
    if (!pending) {
      pending = withTimeout(fetch('https://atlas.shopifysvc.com/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(10000) : undefined,
        body: JSON.stringify({
          query: 'query countries($locale: SupportedLocale!) { countries(locale: $locale) { name code zones { name code } } }',
          variables: { locale: 'EN' }
        })
      }), 10000)
        .then(function (response) {
          if (!response.ok) throw new Error(messages.unavailable);
          return response.json();
        })
        .then(function (body) {
          if (!body.data || !Array.isArray(body.data.countries)) throw new Error(messages.unavailable);
          return body.data.countries;
        })
        .catch(function (error) {
          pending = null;
          throw error;
        });
    }
    return pending;
  }

  function option(value, label) {
    var node = document.createElement('option');
    node.value = value;
    node.textContent = label || value;
    return node;
  }

  function countryIn(all, value) {
    return all.find(function (item) {
      return key(item.code) === key(value) || key(item.name) === key(value);
    });
  }

  function zoneIn(country, value) {
    return (country.zones || []).find(function (item) {
      return key(item.code) === key(value) || key(item.name) === key(value);
    });
  }

  function replaceOptions(input, list, values, labelFor) {
    var target = input && input.tagName === 'SELECT' ? input : list;
    if (!target) return;
    var current = input ? input.value : '';
    target.replaceChildren();
    if (input && input.tagName === 'SELECT') {
      var placeholder = option('', input.dataset.pfPlaceholder || 'Select');
      placeholder.disabled = true;
      target.appendChild(placeholder);
    }
    values.forEach(function (value) {
      target.appendChild(option(value, labelFor ? labelFor(value) : value));
    });
    if (input && values.some(function (value) { return key(value) === key(current); })) {
      input.value = values.find(function (value) { return key(value) === key(current); });
    } else if (input && input.tagName === 'SELECT') {
      input.value = '';
    }
  }

  function bind(form) {
    if (!form || form.dataset.pfRegionsBound === 'true') return Promise.resolve();
    form.dataset.pfRegionsBound = 'true';
    var countryInput = form.elements.namedItem('country') ||
      form.querySelector('[data-pf-co-field="country"], [data-pf-field="countryCode"]');
    var stateInput = form.querySelector('[data-pf-address-state]');
    var cityInput = form.querySelector('[data-pf-address-city]');
    var stateList = form.querySelector('[data-pf-address-state-options]');
    var zipInput = form.querySelector('[data-pf-field="zip"], [data-pf-co-field="zip"]');
    var syncRevision = 0;
    var lookupRevision = 0;
    var stateEditRevision = 0;
    var lastZip = '';
    var hasStates = false;
    if (!countryInput || !stateInput || !cityInput) return Promise.resolve();
    var cityPlaceholder = cityInput.getAttribute('placeholder') || '';

    // City is locked here, not in the markup, so the form still works when
    // this script never runs. The placeholder says why it is locked.
    function updateAvailability() {
      var country = key(countryInput.value);
      var pinRequired = zipInput && (country === 'in' || country === 'india');
      var ready = !pinRequired || /^[1-9]\d{5}$/.test(zipInput.value.trim());
      cityInput.disabled = !ready;
      stateInput.disabled = !ready || !hasStates;
      if (messages.pinFirst) cityInput.setAttribute('placeholder', ready ? cityPlaceholder : messages.pinFirst);
    }

    function sync() {
      updateAvailability();
      var revision = ++syncRevision;
      return countries().then(function (all) {
        if (revision !== syncRevision) return;
        var currentState = stateInput.dataset.pfPendingValue || stateInput.value;
        var country = countryIn(all, countryInput.value);
        var zones = country ? country.zones || [] : [];
        var zone = country ? zoneIn(country, currentState) : null;
        replaceOptions(stateInput, stateList, zones.map(function (item) { return item.name; }));
        hasStates = !!country && zones.length > 0;
        updateAvailability();
        if (zone) {
          stateInput.value = zone.name;
          delete stateInput.dataset.pfPendingValue;
        }
      });
    }

    function clearAutofilled() {
      [stateInput, cityInput].forEach(function (input) {
        if (input.dataset.pfAutofilled !== 'true') return;
        delete input.dataset.pfAutofilled;
        delete input.dataset.pfPendingValue;
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }

    function autofill() {
      var zip = zipInput.value.trim();
      updateAvailability();
      if (zip !== lastZip) {
        lastZip = zip;
        lookupRevision += 1;
        delete zipInput.dataset.pfLookedUp;
        clearAutofilled();
      }
      if (!/^[1-9]\d{5}$/.test(zip) || zip === zipInput.dataset.pfLookedUp) return;
      if (key(countryInput.value) !== 'in' && key(countryInput.value) !== 'india') return;
      if (!window.PFApi || !window.PFApi.isConfigured()) return;
      zipInput.dataset.pfLookedUp = zip;
      var revision = lookupRevision;
      var stateEdits = stateEditRevision;
      withTimeout(window.PFApi.get('/v1/locations/pincode/' + zip), 8000).then(function (body) {
        if (revision !== lookupRevision || zipInput.value.trim() !== zip || !body || !body.location) return;
        if (cityInput.dataset.pfRestoredPin === zip && cityInput.value === cityInput.dataset.pfRestoredCity &&
            body.location.city && key(cityInput.value) === key(body.location.city)) {
          cityInput.dataset.pfAutofilled = 'true';
        }
        if (stateEdits === stateEditRevision) {
          stateInput.dataset.pfPendingValue = body.location.provinceCode;
          stateInput.dataset.pfAutofilled = 'true';
        }
        if (body.location.city && (!cityInput.value.trim() || cityInput.dataset.pfAutofilled === 'true')) {
          cityInput.value = body.location.city;
          cityInput.dispatchEvent(new Event('input', { bubbles: true }));
          cityInput.dataset.pfAutofilled = 'true';
        }
        form.dispatchEvent(new CustomEvent('pf:address-autofilled', { bubbles: true }));
        return sync().then(function () {
          if (revision === lookupRevision && stateEdits === stateEditRevision) {
            stateInput.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
      }).catch(function (error) {
        if (revision !== lookupRevision) return;
        delete zipInput.dataset.pfLookedUp;
        if (error && error.status === 400) clearAutofilled();
      });
    }

    function syncFromEvent() {
      sync().catch(function (error) {
        form.dispatchEvent(new CustomEvent('pf:address-region-error', { detail: error }));
      });
    }
    countryInput.addEventListener('change', function () {
      lookupRevision += 1;
      if (zipInput) delete zipInput.dataset.pfLookedUp;
      clearAutofilled();
      stateInput.value = '';
      delete stateInput.dataset.pfPendingValue;
      syncFromEvent();
      if (zipInput) autofill();
    });
    function cityEdited() {
      delete cityInput.dataset.pfAutofilled;
      delete cityInput.dataset.pfRestoredPin;
      delete cityInput.dataset.pfRestoredCity;
    }
    cityInput.addEventListener('input', cityEdited);
    cityInput.addEventListener('change', cityEdited);
    stateInput.addEventListener('change', function () {
      stateEditRevision += 1;
      delete stateInput.dataset.pfAutofilled;
      delete stateInput.dataset.pfPendingValue;
    });
    if (zipInput) {
      zipInput.addEventListener('input', autofill);
      zipInput.addEventListener('change', autofill);
      zipInput.addEventListener('blur', autofill);
      autofill(); // a PIN typed before this script loaded (slow phones) still autofills
    }
    form.pfSyncAddressRegions = sync;
    return sync();
  }

  window.PFAddressRegions = {
    bind: bind,
    sync: function (form, options) {
      if (!form.pfSyncAddressRegions) {
        return bind(form).then(function () {
          return form.pfSyncAddressRegions ? form.pfSyncAddressRegions(options) : undefined;
        });
      }
      return form.pfSyncAddressRegions(options);
    },
    resolve: function (countryValue, provinceValue, cityValue, zipValue) {
      return countries().then(function (all) {
        var country = countryIn(all, countryValue);
        if (!country) throw fieldError('countryCode', messages.country);
        var zone = zoneIn(country, provinceValue);
        if (country.zones.length && !zone) throw fieldError('provinceCode', messages.province);
        var city = String(cityValue || '').trim();
        if (country.code === 'IN') {
          if (cityValue === undefined || zipValue === undefined) {
            return {
              countryCode: country.code,
              provinceCode: zone ? zone.code : undefined,
              province: zone ? zone.name : provinceValue,
              city: city
            };
          }
          if (!window.PFApi || !window.PFApi.isConfigured()) throw new Error(messages.unavailable);
          return window.PFApi.post('/v1/locations/india', {
            operation: 'validate',
            countryCode: country.code,
            province: zone.name,
            city: city,
            zip: String(zipValue || '').trim()
          }).then(function (body) {
            return body.location;
          });
        }
        return {
          countryCode: country.code,
          province: zone ? zone.name : provinceValue,
          city: city
        };
      });
    }
  };
})();
